import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Car, Clock, ShowerHead, ArrowRight, TrainFront } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, speakableSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, CtaBand, FaqList, PageHero, Crumbs } from "../components/Shared";
import { QuickAnswer } from "../components/QuickAnswer";
import { TownQuotes } from "../components/TownQuotes";
import { SITE, IMG } from "../data/mock";

const NEIGHBORHOODS = [
  {
    name: "Dedham",
    time: "5 min",
    route:
      "Head north on Providence Highway / VFW Parkway past Home Depot — we're on the right just past the Dedham line. From Legacy Place or Dedham Square it's about a 7-minute drive.",
  },
  {
    name: "Roslindale",
    time: "8 min",
    route:
      "From Roslindale Village, take Washington Street to the West Roxbury Parkway, or Centre Street straight through — either way you're parked and warming up in under ten minutes.",
  },
  {
    name: "Hyde Park",
    time: "10 min",
    route:
      "Cut through the Stony Brook Reservation via Enneking Parkway to Washington Street, then follow the Parkway to VFW Parkway. A quiet, lights-free drive most of the day.",
  },
  {
    name: "Westwood",
    time: "10 min",
    route:
      "Straight up Providence Highway (Route 1) through Dedham — the highway becomes VFW Parkway and we're right on it. Easy on, easy off.",
  },
  {
    name: "Brookline",
    time: "12 min",
    route:
      "From Putterham Circle or Chestnut Hill, take the West Roxbury Parkway south to Spring Street, then VFW Parkway. Most Brookline members beat downtown traffic entirely.",
  },
  {
    name: "Needham",
    time: "12 min",
    route:
      "Great Plain Avenue to Dedham Avenue, cross the Charles at the Bridge Street rotary, and you're two minutes from the gym. Needham Heights adds about five minutes.",
  },
  {
    name: "Jamaica Plain",
    time: "12 min",
    route:
      "Centre Street through the Arnold Arboretum straight into West Roxbury, or the 36 bus along the same corridor. JP members love the 5:15 AM class — zero traffic.",
  },
  {
    name: "Newton",
    time: "14 min",
    route:
      "From Oak Hill or Newton Upper Falls, take Nahanton Street to Winchester/Spring Street and follow it to VFW Parkway. From Newton Centre, Parker Street south works best.",
  },
];

const PROGRAM_LINKS = [
  { name: "CrossFit Classes", to: "/classes/crossfit" },
  { name: "Beginner Fundamentals", to: "/classes/fundamentals" },
  { name: "Olympic Weightlifting", to: "/classes/olympic-weightlifting" },
  { name: "Powerlifting", to: "/classes/powerlifting" },
  { name: "HYROX Race Training", to: "/hyrox" },
  { name: "Personal Training", to: "/classes/personal-training" },
  { name: "50+ Fitness", to: "/classes/50-plus-fitness" },
  { name: "Live B.I.G. 365", to: "/live-big-365" },
  { name: "Gold Standard Programs", to: "/gold-standard" },
];

const wrFaqs = [
  {
    q: "Where is Boston Iron Grit located in West Roxbury?",
    a: "We're at 1230 VFW Parkway, West Roxbury, MA 02132, with the entrance and free parking lot on Sambuca Way Alley off VFW Parkway — near the Home Depot plaza, just before the Dedham line.",
  },
  {
    q: "Is there parking at your West Roxbury gym?",
    a: "Yes — free on-site parking with the lot entrance on Sambuca Way Alley. No meters, no permits, no circling the block like downtown Boston gyms.",
  },
  {
    q: "How far is the gym from Dedham?",
    a: "About 5 minutes by car. We sit on VFW Parkway just north of the Dedham line — closer to most of Dedham than many gyms in Dedham itself. Legacy Place is roughly a 7-minute drive.",
  },
  {
    q: "Do members come from outside West Roxbury?",
    a: "All the time. Roughly half our members drive in from Dedham, Roslindale, Needham, Newton, Brookline, Westwood, Hyde Park and Jamaica Plain — the Parkway location makes us an easy reverse-commute from every direction.",
  },
  {
    q: "Can I get to the gym by public transit?",
    a: "Yes. The MBTA 36 bus runs along VFW Parkway near the gym, and the Needham Commuter Rail Line stops at West Roxbury, Highland and Bellevue stations, each a short ride or walk away.",
  },
  {
    q: "What's the best way to try the gym if I live in a nearby town?",
    a: "Book a free 20-minute goal review. You'll see the space, meet a coach, talk through your goals and get an honest recommendation — no pressure and no obligation. Visiting CrossFit athletes can also drop in for $35.",
  },
];

export default function WestRoxbury() {
  return (
    <>
      <Seo
        title="West Roxbury Gym Near Dedham & Roslindale | Boston Iron Grit"
        description="Coached strength & conditioning gym on VFW Parkway in West Roxbury — minutes from Dedham, Roslindale, Needham and Newton. Free parking. Book a free goal review."
        path="/west-roxbury-gym"
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "West Roxbury Gym", path: "/west-roxbury-gym" },
          ]),
          faqSchema(wrFaqs),
          speakableSchema("/west-roxbury-gym"),
        ]}
      />

      <PageHero
        label="Our Neighborhood"
        title={
          <>
            West Roxbury's Gym.<br />
            <span className="text-[#9D0423]">Every Town's Secret.</span>
          </>
        }
        sub="Boston Iron Grit sits right on VFW Parkway — which means Dedham, Roslindale, Needham, Newton, Brookline, Westwood, Hyde Park and Jamaica Plain are all a short, traffic-light drive away."
        image={IMG.interior}
      />

      <div className="bg-[#0A0A0B] pb-4">
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <Crumbs items={[{ name: "Home", path: "/" }, { name: "West Roxbury Gym", path: "/west-roxbury-gym" }]} />
        </div>
        <QuickAnswer>
          Boston Iron Grit is a coached strength &amp; conditioning gym at 1230 VFW Parkway, West Roxbury, MA 02132 — with free on-site parking on
          Sambuca Way Alley. It's a 5-minute drive from Dedham, 8 from Roslindale, 10 from Hyde Park and Westwood, and about 12–14 from Needham,
          Brookline, Jamaica Plain and Newton. New members start with a free 20-minute goal review.
        </QuickAnswer>
      </div>

      {/* ---------------- WHY THE PARKWAY ---------------- */}
      <section className="bg-[#0A0A0B] py-16 lg:py-24" data-testid="wr-intro">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <Eyebrow>Why the Parkway Works</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              The gym that skips<br /><span className="text-neutral-600">Boston traffic.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              Most Boston gyms make you fight downtown traffic, hunt for a meter, and pay for the privilege. We built Boston Iron Grit on VFW
              Parkway on purpose: it's the artery that connects West Roxbury to Dedham, Westwood, Needham and the Parkway neighborhoods, with
              our own free lot off Sambuca Way Alley. You pull in, you train, you're home — often faster than members who live "closer" to a
              downtown box.
            </p>
            <p className="mt-4 text-neutral-300 text-base leading-relaxed">
              Since 2011 that location has quietly made us a regional gym, not just a neighborhood one. On any 5:15 AM whiteboard you'll find a
              nurse from Roslindale, a teacher from Dedham, a contractor from Hyde Park and a retiree from Needham — training side by side, then
              hitting the showers and getting on with their day.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Car, title: "Free Parking", text: "On-site lot on Sambuca Way Alley — no meters, no permits." },
                { icon: Clock, title: "5:15 AM – 7:30 PM", text: "First class before sunrise, last class after work, weekends too." },
                { icon: ShowerHead, title: "Showers On-Site", text: "Train early, shower, and go straight to the office." },
                { icon: TrainFront, title: "Transit Friendly", text: "MBTA 36 bus on the Parkway; Needham Line at West Roxbury, Highland & Bellevue." },
              ].map((f) => (
                <div key={f.title} className="p-6 bg-[#111113] border border-white/5">
                  <f.icon size={20} className="text-[#9D0423] mb-4" />
                  <h3 className="font-heading font-bold uppercase text-white text-sm tracking-wide">{f.title}</h3>
                  <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- NEIGHBORHOODS ---------------- */}
      <section className="bg-[#0D0D0F] py-16 lg:py-24 border-y border-white/5" data-testid="wr-neighborhoods">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Getting Here</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight max-w-3xl">
              Drive times from<br /><span className="text-neutral-600">the towns next door.</span>
            </h2>
            <p className="mt-6 text-neutral-400 text-base leading-relaxed max-w-2xl">
              Real routes our members actually drive — not optimistic map estimates. All times are typical off-peak drives to 1230 VFW Parkway.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {NEIGHBORHOODS.map((n, i) => (
              <Reveal key={n.name} delay={i * 60} className="bg-[#0D0D0F]">
                <div className="p-6 h-full" data-testid={`wr-town-${n.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-heading font-extrabold uppercase text-white text-lg tracking-wide">{n.name}</h3>
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#9D0423] whitespace-nowrap">~{n.time}</span>
                  </div>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{n.route}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-14">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-6">// What the neighbors say</p>
            </Reveal>
            <TownQuotes />
          </div>
        </div>
      </section>

      {/* ---------------- PROGRAMS UNDER ONE ROOF ---------------- */}
      <section className="bg-[#0A0A0B] py-16 lg:py-24" data-testid="wr-programs">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Worth the Drive</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              One roof.<br /><span className="text-neutral-600">Every program.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              People don't drive past three other gyms for equipment — they do it for coaching. Under one roof you'll find coached CrossFit
              classes, the Westie Barbell Club for Olympic lifting and powerlifting, HYROX race prep, beginner fundamentals, personal training,
              50+ fitness and the Live B.I.G. 365 wellness &amp; longevity program with labs, InBody testing and physician-guided peptides and
              GLP-1s. Whatever brings you through the door, there's a next step waiting when your goals change.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <BtnPrimary to="/get-started">Book a Free Goal Review</BtnPrimary>
              <BtnGhost to="/pricing">See Pricing</BtnGhost>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="divide-y divide-white/5 border-y border-white/5">
              {PROGRAM_LINKS.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="flex items-center justify-between py-4 px-2 group hover:bg-white/[0.03] transition-colors"
                  data-testid={`wr-program-${p.to.split("/").pop()}`}
                >
                  <span className="text-[15px] text-neutral-300 group-hover:text-white transition-colors">{p.name}</span>
                  <ArrowRight size={15} className="text-neutral-600 group-hover:text-[#9D0423] transition-colors" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- MAP ---------------- */}
      <section className="bg-[#0D0D0F] py-16 lg:py-24 border-y border-white/5" data-testid="wr-map">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Find Us</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              1230 VFW Parkway.<br /><span className="text-neutral-600">Look for the alley.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed max-w-lg" data-speakable="true">
              Boston Iron Grit · 1230 VFW Parkway, West Roxbury, MA 02132 · Entrance and free parking on Sambuca Way Alley · 617-637-8900 ·
              Open Mon–Fri 5:15 AM–7:30 PM, Sat 6:30 AM–12 PM, Sun 8 AM–12 PM.
            </p>
            <div className="mt-8 space-y-3 font-mono text-[13px] text-neutral-300">
              <div className="flex items-center gap-3"><MapPin size={15} className="text-[#9D0423]" /> {SITE.address.full}</div>
              <div className="pl-7 text-neutral-500 text-[12px]">Entrance on Sambuca Way Alley</div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <BtnPrimary href={SITE.mapsUrl}>Get Directions</BtnPrimary>
              <BtnGhost to="/contact">Contact Us</BtnGhost>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative h-[420px] border border-white/10 overflow-hidden">
              <iframe
                title="Boston Iron Grit map — 1230 VFW Parkway, West Roxbury MA"
                src="https://www.google.com/maps?q=1230+VFW+Parkway,+West+Roxbury,+MA+02132&output=embed"
                className="w-full h-full"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(30%)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="bg-[#0A0A0B] py-16 lg:py-24" data-testid="wr-faq">
        <div className="max-w-4xl mx-auto px-6">
          <FaqList faqs={wrFaqs} title="Location & Service Area FAQs" />
        </div>
      </section>

      <CtaBand
        title="From Dedham? Roslindale? Needham?"
        sub="You're closer than you think. Book a free 20-minute goal review, see the space, and find out why members drive past three other gyms to train here."
      />
    </>
  );
}
